
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";

const templateSchema = z.object({
  description: z.string().min(1, "Description is required"),
  quantity: z.number().min(1, "Quantity must be at least 1"),
  unit_price: z.number().min(0, "Unit price must be non-negative"),
  currency: z.enum(["EUR", "GBP", "USD"]),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface Template {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  currency: "EUR" | "GBP" | "USD";
}

interface LineItemFormProps {
  onSubmit: (data: TemplateFormData) => void;
  editingTemplate: Template | null;
  isLoading: boolean;
}

export function LineItemForm({ onSubmit, editingTemplate, isLoading }: LineItemFormProps) {
  const { t, ready } = useTranslation();
  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: editingTemplate ? {
      description: editingTemplate.description,
      quantity: editingTemplate.quantity,
      unit_price: editingTemplate.unit_price,
      currency: editingTemplate.currency,
    } : {
      description: "",
      quantity: 1,
      unit_price: 0,
      currency: "EUR",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#8791F3]">{t("templates.description", "Description")}</FormLabel>
              <FormControl>
                <Input
                  className="bg-white border border-gray-300 p-2 rounded-md"
                  placeholder={t("templates.enter_item_description", "Enter item description")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="quantity"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormItem>
                <FormLabel className="text-[#8791F3]">{t("templates.quantity", "Quantity")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="bg-white border border-gray-300 p-2 rounded-md"
                    min="1"
                    value={value.toString()}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                      onChange(isNaN(val) ? 0 : val);
                    }}
                    {...rest}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unit_price"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormItem>
                <FormLabel className="text-[#8791F3]">{t("templates.unit_price", "Unit Price")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="bg-white border border-gray-300 p-2 rounded-md"
                    step="1"
                    min="0"
                    value={value.toString()}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      onChange(isNaN(val) ? 0 : val);
                    }}
                    {...rest}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#8791F3]">{t("templates.currency", "Currency")}</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                    <SelectValue placeholder={t("templates.select_currency", "Select currency")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end">
        <Button className="bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200" type="submit" disabled={isLoading}>
          {isLoading
            ? editingTemplate
              ? t("templates.updating", "Updating...")
              : t("templates.creating", "Creating...")
            : editingTemplate
            ? t("templates.update_template", "Update Template")
            : t("templates.create_template", "Create Template")}
        </Button>
        </div>
      </form>
    </Form>
  );
}
